import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getAllConfigurations } from '@/services/adeApi';
import type { AdeConfigurationDTO } from '@/types/api';

export interface AbmConnection {
  id: string;
  name: string;
  organization_name: string | null;
  mdm_server_url: string | null;
  device_enrollment_url: string | null;
  is_active: boolean;
  token_expiry: string | null;
  created_at: string | null;
  updated_at: string | null;
  environment: string | null;
  status: string | null;
}

function mapConfigToConnection(c: AdeConfigurationDTO): AbmConnection {
  return {
    id: c.adeAccountId || c.id || '',
    name: c.accountName || '',
    organization_name: c.organizationName || null,
    mdm_server_url: c.mdmServerUrl || null,
    device_enrollment_url: c.deviceEnrollmentUrl || null,
    is_active: !!c.tokenExpiry && new Date(c.tokenExpiry) > new Date(),
    token_expiry: c.tokenExpiry || null,
    created_at: c.createdAt || null,
    updated_at: c.updatedAt || null,
    environment: c.environment || null,
    status: c.status || null,
  };
}

export const useAbmConnections = () => {
  const [connections, setConnections] = useState<AbmConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      setError(null);
      const configs = await getAllConfigurations();
      const mapped = (configs || []).map((c: AdeConfigurationDTO) => mapConfigToConnection(c));
      setConnections(mapped);
    } catch (err) {
      console.error('Error fetching ABM connections:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch ABM connections'));
      toast({ title: 'Error', description: 'Failed to load Apple Business Manager connections', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return {
    connections,
    loading,
    error,
    fetchConnections,
    getActiveConnections: () => connections.filter(conn => conn.is_active),
  };
};
